import { useState } from "react";
import { ArrowUpRight, Check, Info } from "lucide-react";
import { TRAILERS, SITE } from "../../lib/site-data";

const TrailerArt = ({ tone = "red" }) => {
  const palette =
    tone === "gold"
      ? { bg1: "#6E1A26", bg2: "#1F1B16", accent: "#D4A24C" }
      : tone === "dark"
        ? { bg1: "#1F1B16", bg2: "#3A332B", accent: "#D4A24C" }
        : { bg1: "#8B2332", bg2: "#1F1B16", accent: "#D4A24C" };
  return (
    <svg
      viewBox="0 0 400 250"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0 w-full h-full"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <linearGradient id={`bg-${tone}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={palette.bg1} />
          <stop offset="100%" stopColor={palette.bg2} />
        </linearGradient>
      </defs>
      <rect width="400" height="250" fill={`url(#bg-${tone})`} />
      {/* horizon stripes */}
      <g opacity="0.18" stroke={palette.accent} strokeWidth="1">
        <line x1="0" y1="60" x2="400" y2="60" />
        <line x1="0" y1="80" x2="400" y2="80" />
        <line x1="0" y1="100" x2="400" y2="100" />
        <line x1="0" y1="120" x2="400" y2="120" />
      </g>
      {/* sun */}
      <circle cx="320" cy="90" r="42" fill={palette.accent} opacity="0.18" />
      {/* trailer silhouette */}
      <g transform="translate(40,140)">
        <rect x="0" y="10" width="240" height="40" rx="3" fill="#0F0C09" />
        <rect x="0" y="6" width="240" height="6" fill={palette.accent} />
        <rect x="240" y="20" width="32" height="10" fill="#0F0C09" />
        <rect x="270" y="18" width="6" height="14" fill="#0F0C09" />
        {/* wheels */}
        <circle cx="50" cy="60" r="14" fill="#0F0C09" stroke={palette.accent} strokeWidth="2" />
        <circle cx="50" cy="60" r="5" fill={palette.accent} />
        <circle cx="90" cy="60" r="14" fill="#0F0C09" stroke={palette.accent} strokeWidth="2" />
        <circle cx="90" cy="60" r="5" fill={palette.accent} />
        <circle cx="190" cy="60" r="14" fill="#0F0C09" stroke={palette.accent} strokeWidth="2" />
        <circle cx="190" cy="60" r="5" fill={palette.accent} />
      </g>
      {/* ground */}
      <line x1="0" y1="220" x2="400" y2="220" stroke={palette.accent} strokeWidth="1" opacity="0.3" />
    </svg>
  );
};

export const Fleet = () => {
  return (
    <section id="fleet" data-testid="fleet-section" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="eyebrow mb-3">The Fleet</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F1B16]">
              Four trailers, ready to roll.
            </h2>
            <p className="mt-4 text-base text-[#3A332B] leading-relaxed">
              Every trailer comes loaded with the gear you need — straps, ramps,
              winches and a full tire-changing kit. Pick the size that fits your
              load and reserve in seconds.
            </p>
          </div>
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fleet-cta-top"
            className="btn-gold inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold self-start"
          >
            Open Booking Portal <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 md:gap-8">
          {TRAILERS.map((t, i) => (
            <TrailerCard key={t.id} t={t} idx={i} />
          ))}
        </div>

        <p className="mt-8 text-sm text-[#3A332B]/80 max-w-2xl flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 text-[#8B2332] flex-shrink-0" />
          Bookings are managed through our partner portal. Click any
          <strong> Reserve</strong> button to check availability and lock in
          your dates.
        </p>
      </div>
    </section>
  );
};

const TONES = ["red", "gold", "dark"];

const TrailerCard = ({ t, idx = 0 }) => {
  const tone = TONES[idx % TONES.length];
  const [imgOk, setImgOk] = useState(true);
  return (
    <article
      data-testid={`trailer-card-${t.id}`}
      className="card-trailer bg-white border border-[#E5DCC9] rounded-2xl overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#1F1B16]">
        {imgOk && t.image ? (
          <img
            src={t.image}
            alt={t.name}
            onError={() => setImgOk(false)}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 hover:scale-105"
            data-testid={`trailer-img-${t.id}`}
          />
        ) : (
          <TrailerArt tone={tone} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-3 right-3 bg-[#D4A24C] text-[#1F1B16] text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full shadow-md">
          {t.subtitle}
        </div>
        <div className="absolute bottom-3 left-3 bg-[#1F1B16]/85 backdrop-blur-sm text-[#F8F4ED] text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full">
          {t.capacity} cap.
        </div>
      </div>

      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-xl font-bold text-[#1F1B16]">
          {t.name}
        </h3>

        <div className="mt-4 grid grid-cols-3 gap-2 border-y border-[#E5DCC9] py-3">
          {t.rates.map((r) => (
            <div key={r.label} className="text-center">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#8B2332] font-semibold">
                {r.label}
              </div>
              <div className="font-display text-lg font-bold text-[#1F1B16] mt-1">
                {r.price}
              </div>
            </div>
          ))}
        </div>

        <ul className="mt-5 space-y-2 text-sm text-[#3A332B]">
          {t.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 mt-0.5 text-[#8B2332] flex-shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex flex-wrap gap-2">
          {t.spec.map(([k, v]) => (
            <span
              key={k}
              className="text-xs bg-[#F1E9DA] text-[#3A332B] px-2.5 py-1 rounded-md font-medium"
            >
              <span className="text-[#8B2332] font-semibold">{k}:</span> {v}
            </span>
          ))}
        </div>

        {t.note && (
          <p className="mt-4 text-xs text-[#3A332B]/80 italic border-l-2 border-[#D4A24C] pl-3">
            {t.note}
          </p>
        )}

        <div className="mt-6 pt-2 flex gap-2 mt-auto">
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`trailer-reserve-${t.id}`}
            className="btn-brand flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold"
          >
            Reserve <ArrowUpRight className="w-4 h-4" />
          </a>
          <a
            href={SITE.phoneHref}
            data-testid={`trailer-call-${t.id}`}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-semibold border-2 border-[#1F1B16] text-[#1F1B16] hover:bg-[#1F1B16] hover:text-[#F8F4ED] transition"
          >
            Call
          </a>
        </div>
      </div>
    </article>
  );
};
